/**
 * region-properties-popover.ts - Region properties popover (B6).
 *
 * Opens from the region row right-click context menu.
 * Shows: colour swatch, pixel count, family override status,
 * stack membership, and a rename input.
 *
 * Closes on Escape / outside click.
 * Right-click context menu (Create stack / Add to / etc.) remains
 * separate and unaffected.
 *
 * Decision: the legacy row button was removed in R7. Properties now lives in
 * the context menu alongside stack and rename actions.
 */

import { t } from '../i18n';
import type { RegionEntry, RegionStack, Store } from '../state';

// ── Singleton popover element ────────────────────────────────────────────────

let popoverEl: HTMLDivElement | null = null;
let currentCleanup: (() => void) | null = null;
let currentAnchor: HTMLElement | null = null;

function getOrCreatePopover(): HTMLDivElement {
  if (!popoverEl) {
    popoverEl = document.createElement('div');
    popoverEl.className = 'region-props-popover';
    popoverEl.setAttribute('role', 'dialog');
    popoverEl.setAttribute('aria-modal', 'false');
    document.body.appendChild(popoverEl);
  }
  return popoverEl;
}

function closePopover(): void {
  if (currentCleanup) {
    currentCleanup();
    currentCleanup = null;
  }
  if (popoverEl) {
    popoverEl.hidden = true;
    popoverEl.replaceChildren();
  }
  currentAnchor?.setAttribute('aria-expanded', 'false');
  currentAnchor = null;
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Open a region properties popover from the R7 B2 right-click contextmenu.
 *
 * @param anchor - Contextmenu item element used for positioning.
 * @param entry - The RegionEntry to display.
 * @param pixelCount - Pixel count for the region (from geometry cache).
 * @param stacks - All current region stacks (for stack membership display).
 * @param store - Store reference for rename and colour-change actions.
 */
export function openRegionPropertiesPopover(
  anchor: HTMLElement,
  entry: RegionEntry,
  pixelCount: number,
  stacks: RegionStack[],
  store: Store,
): void {
  // Close any existing popover first.
  closePopover();

  const popover = getOrCreatePopover();
  popover.replaceChildren();
  popover.hidden = false;
  currentAnchor = anchor;
  anchor.setAttribute('aria-expanded', 'true');

  // ── Colour swatch + native colour picker ────────────────────────────────
  const swatchRow = document.createElement('div');
  swatchRow.className = 'region-props-row';

  const swatchLabel = document.createElement('span');
  swatchLabel.className = 'region-props-label';
  swatchLabel.textContent = t('label.props.colour');

  const swatchBtn = document.createElement('button');
  swatchBtn.type = 'button';
  swatchBtn.className = 'region-props-swatch';
  const [r, g, b] = entry.rgb;
  swatchBtn.style.background = `rgb(${r}, ${g}, ${b})`;
  swatchBtn.setAttribute('aria-label', t('aria.props.pickColour'));

  const swatchValue = document.createElement('span');
  swatchValue.className = 'region-props-colour-value';

  const hexValue = document.createElement('span');
  hexValue.className = 'region-props-hex';
  hexValue.textContent = rgbToHex(entry.rgb);

  // Hidden colour input behind the swatch button.
  const colourInput = document.createElement('input');
  colourInput.type = 'color';
  colourInput.className = 'region-props-colour-input';
  colourInput.value = rgbToHex(entry.rgb);
  colourInput.setAttribute('aria-hidden', 'true');
  colourInput.tabIndex = -1;

  swatchBtn.addEventListener('click', () => {
    colourInput.click();
  });

  colourInput.addEventListener('change', () => {
    const hex = colourInput.value;
    const newRgb = hexToRgb(hex);
    const entries = store.get().regionEntries.map((e) =>
      e.uuid === entry.uuid ? { ...e, rgb: newRgb } : e,
    );
    store.patch({ regionEntries: entries });
    swatchBtn.style.background = `rgb(${newRgb[0]}, ${newRgb[1]}, ${newRgb[2]})`;
    hexValue.textContent = rgbToHex(newRgb);
  });

  swatchRow.appendChild(swatchLabel);
  swatchValue.appendChild(swatchBtn);
  swatchValue.appendChild(hexValue);
  swatchValue.appendChild(colourInput);
  swatchRow.appendChild(swatchValue);
  popover.appendChild(swatchRow);

  // ── Pixel count ─────────────────────────────────────────────────────────
  if (pixelCount > 0) {
    const pxRow = document.createElement('div');
    pxRow.className = 'region-props-row';

    const pxLabel = document.createElement('span');
    pxLabel.className = 'region-props-label';
    pxLabel.textContent = t('label.props.pixels');

    const pxValue = document.createElement('span');
    pxValue.className = 'region-props-value';
    pxValue.textContent = pixelCount.toLocaleString();

    pxRow.appendChild(pxLabel);
    pxRow.appendChild(pxValue);
    popover.appendChild(pxRow);
  }

  // ── Family override status ───────────────────────────────────────────────
  const overrideRow = document.createElement('div');
  overrideRow.className = 'region-props-row';

  const overrideLabel = document.createElement('span');
  overrideLabel.className = 'region-props-label';
  overrideLabel.textContent = t('label.props.method');

  const overrideValue = document.createElement('span');
  overrideValue.className = 'region-props-value';
  const hasOverride = entry.override !== null;
  if (hasOverride && entry.override) {
    overrideValue.textContent = t('label.props.method.override', { family: entry.override.family });
  } else {
    overrideValue.textContent = t('label.props.method.global');
  }

  overrideRow.appendChild(overrideLabel);
  overrideRow.appendChild(overrideValue);
  popover.appendChild(overrideRow);

  // ── Stack membership ─────────────────────────────────────────────────────
  const stackRow = document.createElement('div');
  stackRow.className = 'region-props-row';

  const stackLabel = document.createElement('span');
  stackLabel.className = 'region-props-label';
  stackLabel.textContent = t('label.props.stack');

  const stackValue = document.createElement('span');
  stackValue.className = 'region-props-value';
  if (entry.stackId !== null) {
    const stack = stacks.find((s) => s.uuid === entry.stackId);
    stackValue.textContent = stack
      ? t('label.props.stack.in', { name: stack.userName })
      : t('label.props.stack.none');
  } else {
    stackValue.textContent = t('label.props.stack.none');
  }

  stackRow.appendChild(stackLabel);
  stackRow.appendChild(stackValue);
  popover.appendChild(stackRow);

  // ── Rename input ─────────────────────────────────────────────────────────
  const renameRow = document.createElement('div');
  renameRow.className = 'region-props-row region-props-rename-row';

  const renameLabel = document.createElement('label');
  renameLabel.className = 'region-props-label';
  renameLabel.htmlFor = 'region-props-rename-input';
  renameLabel.textContent = t('label.props.name');

  const renameInput = document.createElement('input');
  renameInput.type = 'text';
  renameInput.id = 'region-props-rename-input';
  renameInput.className = 'region-props-rename-input';
  renameInput.value = entry.userName;

  renameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closePopover();
    }
  });

  renameInput.addEventListener('blur', commitRename);

  function commitRename(): void {
    const trimmed = renameInput.value.trim();
    if (trimmed && trimmed !== entry.userName) {
      const entries = store.get().regionEntries.map((e) =>
        e.uuid === entry.uuid ? { ...e, userName: trimmed } : e,
      );
      store.patch({ regionEntries: entries });
    }
  }

  renameRow.appendChild(renameLabel);
  renameRow.appendChild(renameInput);
  popover.appendChild(renameRow);

  // ── Position popover near anchor ─────────────────────────────────────────
  const rect = anchor.getBoundingClientRect();
  const popoverWidth = 220;
  const margin = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let left = rect.right + 4;
  if (left + popoverWidth > vw - margin) {
    left = Math.max(margin, rect.left - popoverWidth - 4);
  }
  let top = rect.top;
  // Clamp to viewport bottom (estimate popover height).
  const estimatedHeight = 180;
  if (top + estimatedHeight > vh - margin) {
    top = Math.max(margin, vh - margin - estimatedHeight);
  }

  popover.style.left = `${left}px`;
  popover.style.top = `${top}px`;

  // ── Close on outside click / Escape ─────────────────────────────────────
  function onDocMouseDown(e: MouseEvent): void {
    const target = e.target as Node;
    if (popover.contains(target) || anchor.contains(target)) return;
    closePopover();
  }

  function onDocKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      closePopover();
    }
  }

  document.addEventListener('mousedown', onDocMouseDown, true);
  document.addEventListener('keydown', onDocKeyDown, true);

  currentCleanup = () => {
    document.removeEventListener('mousedown', onDocMouseDown, true);
    document.removeEventListener('keydown', onDocKeyDown, true);
  };

  // Focus the rename input so the popover is immediately actionable.
  renameInput.focus();
  renameInput.select();
}

// ── Colour helpers ───────────────────────────────────────────────────────────

function rgbToHex([r, g, b]: [number, number, number]): string {
  return (
    '#' +
    [r, g, b]
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
  );
}

function hexToRgb(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}
